import type { CityBuilding } from "../../store/gameStore";
import { Timer } from "../shared/Timer";
import { hapticFeedback } from "../../telegram/init";

const BUILDING_ICONS: Record<string, string> = {
  TOWN_HALL:    "\u{1F3DB}\uFE0F",
  ACADEMY:      "\u{1F4DA}",
  BARRACKS:     "\u2694\uFE0F",
  SHIPYARD:     "\u26F5",
  WAREHOUSE:    "\u{1F4E6}",
  TAVERN:       "\u{1F37A}",
  TRADING_POST: "\u{1F3EA}",
  HARBOR:       "\u2693",
  WALL:         "\u{1F9F1}",
  HIDEOUT:      "\u{1F573}\uFE0F",
};

const BUILDING_NAMES: Record<string, string> = {
  TOWN_HALL:    "Town Hall",
  ACADEMY:      "Academy",
  BARRACKS:     "Barracks",
  SHIPYARD:     "Shipyard",
  WAREHOUSE:    "Warehouse",
  TAVERN:       "Tavern",
  TRADING_POST: "Trading Post",
  HARBOR:       "Harbor",
  WALL:         "Wall",
  HIDEOUT:      "Hideout",
};

interface BuildingSlotProps {
  position: number;
  building?: CityBuilding;
  onClick: (position: number) => void;
}

export function BuildingSlot({ position, building, onClick }: BuildingSlotProps) {
  const isEmpty = !building;
  const icon = building ? BUILDING_ICONS[building.buildingType] ?? "\u{1F3E0}" : null;
  const name = building ? BUILDING_NAMES[building.buildingType] ?? building.buildingType : null;

  return (
    <button
      onClick={() => {
        hapticFeedback("light");
        onClick(position);
      }}
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: "3px",
        aspectRatio: "1",
        borderRadius: "var(--radius-lg)",
        border: isEmpty
          ? "2px dashed var(--sand)"
          : "1.5px solid var(--sand)",
        background: isEmpty
          ? "rgba(212, 196, 160, 0.25)"
          : building?.isUpgrading
            ? "linear-gradient(135deg, rgba(201, 169, 97, 0.15), rgba(218, 183, 82, 0.1))"
            : "linear-gradient(135deg, var(--marble), var(--papyrus-light))",
        boxShadow: isEmpty ? "none" : "var(--shadow-soft)",
        position: "relative",
        overflow: "hidden",
        transition: "all 0.2s ease",
        animation: `stagger-in 0.4s ease both`,
        animationDelay: `${position * 50}ms`,
      }}
    >
      {isEmpty ? (
        <>
          <span style={{
            fontSize: "1.3rem",
            opacity: 0.3,
          }}>+</span>
          <span style={{
            fontFamily: "var(--font-display)",
            fontSize: "0.5rem",
            color: "var(--ink-faded)",
            opacity: 0.6,
            textTransform: "uppercase",
            letterSpacing: "0.05em",
          }}>
            Empty
          </span>
        </>
      ) : (
        <>
          {/* Building icon */}
          <span style={{
            fontSize: "1.5rem",
            lineHeight: 1,
            filter: building?.isUpgrading ? "none" : "none",
          }}>
            {icon}
          </span>

          {/* Building name */}
          <span style={{
            fontFamily: "var(--font-display)",
            fontSize: "0.48rem",
            color: "var(--ink-light)",
            textTransform: "uppercase",
            letterSpacing: "0.03em",
            lineHeight: 1.1,
            textAlign: "center",
            maxWidth: "100%",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            padding: "0 2px",
          }}>
            {name}
          </span>

          {/* Level badge */}
          <span style={{
            position: "absolute",
            top: "3px",
            right: "3px",
            background: building.level === 0 ? "var(--gold)" : "var(--terra)",
            color: "var(--papyrus-light)",
            fontSize: "0.55rem",
            fontWeight: 700,
            fontFamily: "var(--font-display)",
            minWidth: "16px",
            height: "16px",
            borderRadius: "8px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "0 3px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
          }}>
            {building.level === 0 ? "..." : building.level}
          </span>

          {/* Upgrade indicator */}
          {building.isUpgrading && building.upgradeEndAt && (
            <span style={{
              position: "absolute",
              bottom: "3px",
              left: "50%",
              transform: "translateX(-50%)",
            }}>
              <Timer endAt={building.upgradeEndAt} compact />
            </span>
          )}

          {/* Shimmer when upgrading */}
          {building.isUpgrading && (
            <span style={{
              position: "absolute",
              inset: 0,
              background: "linear-gradient(90deg, transparent, rgba(201, 169, 97, 0.15), transparent)",
              backgroundSize: "200% 100%",
              animation: "shimmer 2s infinite linear",
              pointerEvents: "none",
            }} />
          )}
        </>
      )}
    </button>
  );
}

export { BUILDING_ICONS, BUILDING_NAMES };
